import type { JsonObject, ToolContext, ToolDef } from "@fabric-harness/sdk";
import type {
  AccessControlRequest,
  AccessControlResponse,
  AccessManagementClient,
  Permission,
  PermissionsResponse,
} from "@databricks/sdk-accessmanagement/v1";
import type { GenieClient } from "@databricks/sdk-genie/v1";

export interface DatabricksGenieAdminClients {
  genie: Pick<
    GenieClient,
    "genieListSpaces" | "genieGetSpace" | "createSpace" | "updateSpace" | "genieTrashSpace"
  >;
  permissions: Pick<AccessManagementClient, "getObjectPermissions" | "updateObjectPermissions">;
}
import {
  DatabricksManagedResourceConflictError,
  type DatabricksManagedResourceStore,
  DatabricksUnmanagedResourceError,
  databricksResourceFingerprint,
  requireDatabricksManagedResource,
} from "./managed-resource.js";

export interface DatabricksGenieAgentSpecV2 {
  version: 2;
  title: string;
  description?: string;
  parentPath: string;
  warehouseId: string;
  sampleQuestions?: Array<{ id?: string; question: string }>;
  dataSources: DatabricksGenieDataSource[];
  /** Databricks currently permits at most one text instruction. */
  instructions?: string[];
  examples?: DatabricksGenieSqlExample[];
  sqlFunctions?: DatabricksGenieSqlFunction[];
  joins?: DatabricksGenieJoinSpec[];
  sqlSnippets?: DatabricksGenieSqlSnippets;
  benchmarks?: DatabricksGenieBenchmark[];
}

export interface DatabricksGenieSqlExample {
  id?: string;
  question: string;
  sql: string;
}
export interface DatabricksGenieSqlFunction {
  id?: string;
  identifier: string;
}
export interface DatabricksGenieJoinSide {
  identifier: string;
  alias?: string;
}
export interface DatabricksGenieJoinSpec {
  id?: string;
  left: DatabricksGenieJoinSide;
  right: DatabricksGenieJoinSide;
  sql: string;
}
export interface DatabricksGenieSqlSnippet {
  id?: string;
  sql: string;
  displayName?: string;
  alias?: string;
  synonyms?: string[];
}
export interface DatabricksGenieSqlSnippets {
  filters?: DatabricksGenieSqlSnippet[];
  expressions?: DatabricksGenieSqlSnippet[];
  measures?: DatabricksGenieSqlSnippet[];
}
export interface DatabricksGenieBenchmark {
  id?: string;
  question: string;
  sql: string;
}

export interface DatabricksGenieSqlPolicyInput {
  sql: string;
  purpose: "example" | "join" | "filter" | "expression" | "measure" | "benchmark";
  declaredDataSources: string[];
}
export interface DatabricksGenieSqlPolicyResult {
  resources: string[];
}
export type DatabricksGenieSqlPolicy = (
  input: DatabricksGenieSqlPolicyInput,
) => DatabricksGenieSqlPolicyResult | Promise<DatabricksGenieSqlPolicyResult>;

export interface DatabricksGenieDataSource {
  kind?: "table" | "metric-view";
  table: string;
  description?: string;
  columns?: DatabricksGenieColumnSpec[];
}

export interface DatabricksGenieColumnSpec {
  name: string;
  description?: string;
  synonyms?: string[];
  exclude?: boolean;
  getExampleValues?: boolean;
  enableFormatAssistance?: boolean;
  enableEntityMatching?: boolean;
}

export interface DatabricksGenieAgent {
  agentId: string;
  title: string;
  description?: string;
  parentPath?: string;
  warehouseId?: string;
  etag?: string;
  serializedSpace?: string;
  fingerprint: string;
}

export interface DatabricksGenieAgentPage {
  agents: DatabricksGenieAgent[];
  nextPageToken?: string;
}

export type DatabricksGeniePermissionLevel = "CAN_VIEW" | "CAN_RUN" | "CAN_EDIT" | "CAN_MANAGE";

export interface DatabricksGeniePermissionChange {
  permissionLevel: DatabricksGeniePermissionLevel;
  userName?: string;
  groupName?: string;
  servicePrincipalName?: string;
}

export interface DatabricksGeniePermissions {
  objectId?: string;
  objectType?: string;
  accessControlList: DatabricksGenieAccessControl[];
}

export interface DatabricksGenieAccessControl {
  userName?: string;
  groupName?: string;
  servicePrincipalName?: string;
  displayName?: string;
  allPermissions: Array<{
    permissionLevel?: string;
    inherited?: boolean;
    inheritedFromObject?: string[];
  }>;
}

export interface DatabricksGenieExport {
  exportedAt: string;
  agent: DatabricksGenieAgent;
  serializedSpace: string;
}

export interface DatabricksGenieOwnershipContext {
  principal: string;
  toolCallId: string;
}

export interface DatabricksGenieAdminOptions {
  managedResourceStore?: DatabricksManagedResourceStore;
}

export interface DatabricksGenieAuthoringToolOptions {
  managedResourceStore: DatabricksManagedResourceStore;
  principal: string;
  destructive?: boolean;
  /** Required before SQL-bearing fields are admitted to model-facing create/update schemas. */
  sqlPolicy?: DatabricksGenieSqlPolicy;
}

export interface DatabricksGenieAgentUpdate {
  expectedFingerprint: string;
  set: {
    spec?: DatabricksGenieAgentSpecV2;
    title?: string;
    description?: string;
    parentPath?: string;
    warehouseId?: string;
  };
  /** The management API has no general field mask; only description supports explicit clearing. */
  remove?: Array<"description">;
}

interface RawGenieAgent {
  space_id?: unknown;
  id?: unknown;
  title?: unknown;
  description?: unknown;
  parent_path?: unknown;
  warehouse_id?: unknown;
  etag?: unknown;
  serialized_space?: unknown;
}

const GENIE_RESOURCE_TYPE = "genie-agent";
const SERIALIZED_VERSION = 2;
const ID_PATTERN = /^[0-9a-f]{32}$/;
const MAX_STRING_LENGTH = 25_000;
const UPDATE_STABILITY_WINDOW_MS = 2_000;

export class DatabricksGenieAdmin {
  constructor(
    private readonly clients: DatabricksGenieAdminClients,
    private readonly options: DatabricksGenieAdminOptions = {},
  ) {}

  async list(
    options: { pageSize?: number; pageToken?: string; signal?: AbortSignal } = {},
  ): Promise<DatabricksGenieAgentPage> {
    const response = snakeRecord(
      await this.clients.genie.genieListSpaces(
        {
          pageSize: options.pageSize,
          pageToken: options.pageToken,
        },
        signalOption(options.signal),
      ),
    );
    const rawAgents = Array.isArray(response.spaces) ? response.spaces : [];
    const agents = await Promise.all(
      rawAgents.map((raw) => parseAgent(requireRecord(raw, "space"))),
    );
    return {
      agents,
      ...optional("nextPageToken", asString(response.next_page_token)),
    };
  }

  async get(
    agentId: string,
    options: { includeConfiguration?: boolean; signal?: AbortSignal } = {},
  ): Promise<DatabricksGenieAgent> {
    const raw = snakeRecord(
      await this.clients.genie.genieGetSpace(
        {
          spaceId: nonEmpty(agentId, "agentId"),
          includeSerializedSpace: options.includeConfiguration,
        },
        signalOption(options.signal),
      ),
    ) as RawGenieAgent;
    return parseAgent(raw);
  }

  async create(
    spec: DatabricksGenieAgentSpecV2,
    options: { ownership?: DatabricksGenieOwnershipContext; signal?: AbortSignal } = {},
  ): Promise<DatabricksGenieAgent> {
    const normalized = await normalizeDatabricksGenieAgentSpec(spec);
    const serializedSpace = JSON.stringify(serializedSpec(normalized));
    const raw = snakeRecord(
      await this.clients.genie.createSpace(
        {
          title: normalized.title,
          ...optional("description", normalized.description),
          parentPath: normalized.parentPath,
          warehouseId: normalized.warehouseId,
          serializedSpace,
        },
        signalOption(options.signal),
      ),
    ) as RawGenieAgent;
    const created = await parseAgent({
      ...raw,
      title: raw.title ?? normalized.title,
      description: raw.description ?? normalized.description,
      parent_path: raw.parent_path ?? normalized.parentPath,
      warehouse_id: raw.warehouse_id ?? normalized.warehouseId,
      serialized_space: raw.serialized_space ?? serializedSpace,
    });
    await this.recordOwnership(created, options.ownership);
    return created;
  }

  async update(
    agentId: string,
    update: DatabricksGenieAgentUpdate,
    options: { ownership?: DatabricksGenieOwnershipContext; signal?: AbortSignal } = {},
  ): Promise<DatabricksGenieAgent> {
    nonEmpty(update.expectedFingerprint, "expectedFingerprint");
    if (Object.keys(update.set).length === 0 && !update.remove?.length)
      throw new Error("Databricks Genie update requires at least one set or remove operation.");
    const current = await this.get(agentId, {
      includeConfiguration: true,
      ...signalOption(options.signal),
    });
    if (current.fingerprint !== update.expectedFingerprint) {
      throw new DatabricksManagedResourceConflictError({
        resourceType: GENIE_RESOURCE_TYPE,
        resourceId: current.agentId,
        expectedFingerprint: update.expectedFingerprint,
        actualFingerprint: current.fingerprint,
      });
    }

    let normalized: DatabricksGenieAgentSpecV2 | undefined;
    if (update.set.spec) normalized = await normalizeDatabricksGenieAgentSpec(update.set.spec);
    const body = {
      ...optional("etag", current.etag),
      ...optional("title", normalized?.title ?? update.set.title),
      ...optional("description", normalized?.description ?? update.set.description),
      ...optional("parent_path", normalized?.parentPath ?? update.set.parentPath),
      ...optional("warehouse_id", normalized?.warehouseId ?? update.set.warehouseId),
      ...(normalized ? { serialized_space: JSON.stringify(serializedSpec(normalized)) } : {}),
      ...(update.remove?.includes("description") ? { description: "" } : {}),
    };
    const rawUpdated = snakeRecord(
      await this.clients.genie.updateSpace(
        {
          spaceId: current.agentId,
          etag: body.etag as string | undefined,
          title: body.title as string | undefined,
          description: body.description as string | undefined,
          parentPath: body.parent_path as string | undefined,
          warehouseId: body.warehouse_id as string | undefined,
          serializedSpace: body.serialized_space as string | undefined,
        },
        signalOption(options.signal),
      ),
    ) as RawGenieAgent;
    const acknowledged = await parseAgent({
      ...rawUpdated,
      space_id: rawUpdated.space_id ?? current.agentId,
      title: rawUpdated.title ?? body.title ?? current.title,
      description: rawUpdated.description ?? body.description ?? current.description,
      parent_path: rawUpdated.parent_path ?? body.parent_path ?? current.parentPath,
      warehouse_id: rawUpdated.warehouse_id ?? body.warehouse_id ?? current.warehouseId,
      etag: rawUpdated.etag ?? current.etag,
      serialized_space:
        rawUpdated.serialized_space ?? body.serialized_space ?? current.serializedSpace,
    });
    const updated = await this.waitForFingerprint(
      current.agentId,
      acknowledged.fingerprint,
      options.signal,
    );
    await this.recordOwnership(updated, options.ownership, current);
    return updated;
  }

  private async waitForFingerprint(
    agentId: string,
    expectedFingerprint: string,
    signal?: AbortSignal,
  ): Promise<DatabricksGenieAgent> {
    const deadline = Date.now() + 30_000;
    let firstMatchingReadAt: number | undefined;
    for (;;) {
      const visible = await this.get(agentId, {
        includeConfiguration: true,
        ...signalOption(signal),
      });
      if (visible.fingerprint === expectedFingerprint) {
        firstMatchingReadAt ??= Date.now();
        if (Date.now() - firstMatchingReadAt >= UPDATE_STABILITY_WINDOW_MS) return visible;
      } else {
        firstMatchingReadAt = undefined;
      }
      if (Date.now() >= deadline) {
        throw new DatabricksManagedResourceConflictError({
          resourceType: GENIE_RESOURCE_TYPE,
          resourceId: agentId,
          expectedFingerprint,
          actualFingerprint: visible.fingerprint,
        });
      }
      await abortableDelay(250, signal);
    }
  }

  async delete(agentId: string, options: { signal?: AbortSignal } = {}): Promise<void> {
    const normalizedId = nonEmpty(agentId, "agentId");
    try {
      await this.clients.genie.genieTrashSpace(
        { spaceId: normalizedId },
        signalOption(options.signal),
      );
    } catch (error) {
      if (!isGenieAbsentError(error)) throw error;
    }
    await this.waitUntilTrashed(normalizedId, options.signal);
    await this.options.managedResourceStore?.delete(GENIE_RESOURCE_TYPE, normalizedId);
  }

  private async waitUntilTrashed(agentId: string, signal?: AbortSignal): Promise<void> {
    const deadline = Date.now() + 30_000;
    for (;;) {
      let pageToken: string | undefined;
      let active = false;
      do {
        const page = await this.list({
          pageSize: 100,
          ...optional("pageToken", pageToken),
          ...signalOption(signal),
        });
        if (page.agents.some((agent) => agent.agentId === agentId)) {
          active = true;
          break;
        }
        pageToken = page.nextPageToken;
      } while (pageToken);
      if (!active) return;
      if (Date.now() >= deadline)
        throw new Error(`Databricks Genie Agent ${agentId} remained active after trash request.`);
      await abortableDelay(250, signal);
    }
  }

  async permissions(
    agentId: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<DatabricksGeniePermissions> {
    const raw = await this.clients.permissions.getObjectPermissions(
      { requestObjectType: "genie", requestObjectId: nonEmpty(agentId, "agentId") },
      signalOption(options.signal),
    );
    return parsePermissions(raw);
  }

  async updatePermissions(
    agentId: string,
    changes: DatabricksGeniePermissionChange[],
    options: { signal?: AbortSignal } = {},
  ): Promise<DatabricksGeniePermissions> {
    if (changes.length === 0)
      throw new Error("Databricks Genie permission changes cannot be empty.");
    const raw = await this.clients.permissions.updateObjectPermissions(
      {
        requestObjectType: "genie",
        requestObjectId: nonEmpty(agentId, "agentId"),
        accessControlList: changes.map(permissionChangeToSdk),
      },
      signalOption(options.signal),
    );
    return parsePermissions(raw);
  }

  async export(
    agentId: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<DatabricksGenieExport> {
    const agent = await this.get(agentId, {
      includeConfiguration: true,
      ...signalOption(options.signal),
    });
    if (!agent.serializedSpace)
      throw new Error(`Databricks Genie Agent ${agent.agentId} did not return serialized_space.`);
    return { exportedAt: new Date().toISOString(), agent, serializedSpace: agent.serializedSpace };
  }

  /** Raw export import is intentionally typed-client-only; no model ToolDef accepts serialized space. */
  async import(
    exported: DatabricksGenieExport,
    options: {
      title?: string;
      description?: string;
      parentPath?: string;
      warehouseId?: string;
      ownership?: DatabricksGenieOwnershipContext;
      signal?: AbortSignal;
    } = {},
  ): Promise<DatabricksGenieAgent> {
    validateSerializedSpace(exported.serializedSpace);
    const raw = snakeRecord(
      await this.clients.genie.createSpace(
        {
          title: options.title ?? exported.agent.title,
          ...optional("description", options.description ?? exported.agent.description),
          ...optional("parentPath", options.parentPath ?? exported.agent.parentPath),
          ...optional("warehouseId", options.warehouseId ?? exported.agent.warehouseId),
          serializedSpace: exported.serializedSpace,
        },
        signalOption(options.signal),
      ),
    ) as RawGenieAgent;
    const created = await parseAgent({
      ...raw,
      title: raw.title ?? options.title ?? exported.agent.title,
      description: raw.description ?? options.description ?? exported.agent.description,
      parent_path: raw.parent_path ?? options.parentPath ?? exported.agent.parentPath,
      warehouse_id: raw.warehouse_id ?? options.warehouseId ?? exported.agent.warehouseId,
      serialized_space: raw.serialized_space ?? exported.serializedSpace,
    });
    await this.recordOwnership(created, options.ownership);
    return created;
  }

  private async recordOwnership(
    agent: DatabricksGenieAgent,
    ownership?: DatabricksGenieOwnershipContext,
    previous?: DatabricksGenieAgent,
  ): Promise<void> {
    if (!this.options.managedResourceStore || !ownership) return;
    const now = new Date().toISOString();
    const existing = await this.options.managedResourceStore.get(
      GENIE_RESOURCE_TYPE,
      agent.agentId,
    );
    await this.options.managedResourceStore.put({
      resourceType: GENIE_RESOURCE_TYPE,
      resourceId: agent.agentId,
      fingerprint: agent.fingerprint,
      createdBy: existing?.createdBy ?? ownership.principal,
      toolCallId: ownership.toolCallId,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
      metadata: {
        title: agent.title,
        ...optional("previousFingerprint", previous?.fingerprint),
      },
    });
  }
}

export function databricksGenieAdmin(
  client: DatabricksGenieAdminClients,
  options: DatabricksGenieAdminOptions = {},
): DatabricksGenieAdmin {
  return new DatabricksGenieAdmin(client, options);
}

export function databricksGenieAuthoringTools(
  client: DatabricksGenieAdminClients,
  options: DatabricksGenieAuthoringToolOptions,
): ToolDef[] {
  const admin = databricksGenieAdmin(client, {
    managedResourceStore: options.managedResourceStore,
  });
  const tools: ToolDef[] = [
    genieTool(
      "databricks_create_genie_agent",
      "Create a governed Genie Agent from a normalized configuration without arbitrary SQL.",
      {
        type: "object",
        additionalProperties: false,
        properties: { spec: genieSpecSchema(Boolean(options.sqlPolicy)) },
        required: ["spec"],
      },
      [
        { kind: "workspace-path", path: "/spec/parentPath" },
        { kind: "warehouse", path: "/spec/warehouseId" },
        { kind: "table", path: "/spec/dataSources/*/table", catalogQualified: true },
      ],
      "write",
      async (input, context) => {
        const spec = input.spec as unknown as DatabricksGenieAgentSpecV2;
        if (options.sqlPolicy) await validateDatabricksGenieSqlReferences(spec, options.sqlPolicy);
        const created = await admin.create(spec, {
          ownership: ownership(options.principal, context),
          ...contextSignal(context),
        });
        return publicAgent(created);
      },
    ),
    genieTool(
      "databricks_get_genie_agent",
      "Inspect one Genie Agent and return its conflict-safe fingerprint.",
      idSchema(false),
      [{ kind: "genie-agent", path: "/agentId" }],
      "read",
      async (input, context) =>
        publicAgent(
          await admin.get(String(input.agentId), {
            includeConfiguration: true,
            ...contextSignal(context),
          }),
        ),
    ),
    genieTool(
      "databricks_update_genie_agent",
      "Update a Harness-managed Genie Agent using its expected fingerprint.",
      updateToolSchema(Boolean(options.sqlPolicy)),
      [
        { kind: "genie-agent", path: "/agentId" },
        { kind: "workspace-path", path: "/set/spec/parentPath", optional: true },
        { kind: "warehouse", path: "/set/spec/warehouseId", optional: true },
        {
          kind: "table",
          path: "/set/spec/dataSources/*/table",
          catalogQualified: true,
          optional: true,
        },
      ],
      "write",
      async (input, context) => {
        const agentId = String(input.agentId);
        const expectedFingerprint = String(input.expectedFingerprint);
        const nextSpec = (input.set as DatabricksGenieAgentUpdate["set"]).spec;
        if (nextSpec && options.sqlPolicy)
          await validateDatabricksGenieSqlReferences(nextSpec, options.sqlPolicy);
        await requireDatabricksManagedResource({
          store: options.managedResourceStore,
          resourceType: GENIE_RESOURCE_TYPE,
          resourceId: agentId,
          expectedFingerprint,
        });
        const updated = await admin.update(
          agentId,
          {
            expectedFingerprint,
            set: input.set as DatabricksGenieAgentUpdate["set"],
            ...(Array.isArray(input.remove)
              ? { remove: input.remove as Array<"description"> }
              : {}),
          },
          {
            ownership: ownership(options.principal, context),
            ...contextSignal(context),
          },
        );
        return publicAgent(updated);
      },
    ),
  ];
  if (options.destructive) {
    tools.push(
      genieTool(
        "databricks_delete_genie_agent",
        "Delete one Harness-managed Genie Agent by exact id and expected fingerprint.",
        idSchema(true),
        [{ kind: "genie-agent", path: "/agentId" }],
        "write",
        async (input, context) => {
          const agentId = String(input.agentId);
          const expectedFingerprint = String(input.expectedFingerprint);
          try {
            await requireDatabricksManagedResource({
              store: options.managedResourceStore,
              resourceType: GENIE_RESOURCE_TYPE,
              resourceId: agentId,
              expectedFingerprint,
            });
          } catch (error) {
            if (error instanceof DatabricksUnmanagedResourceError) {
              try {
                await admin.get(agentId, contextSignal(context));
              } catch (lookupError) {
                if (isGenieAbsentError(lookupError))
                  return { deleted: true, agentId, alreadyAbsent: true };
                throw error;
              }
            }
            throw error;
          }
          let current: DatabricksGenieAgent;
          try {
            current = await admin.get(agentId, {
              includeConfiguration: true,
              ...contextSignal(context),
            });
          } catch (lookupError) {
            if (!isGenieAbsentError(lookupError)) throw lookupError;
            await options.managedResourceStore.delete(GENIE_RESOURCE_TYPE, agentId);
            return { deleted: true, agentId, alreadyAbsent: true };
          }
          if (current.fingerprint !== expectedFingerprint) {
            throw new DatabricksManagedResourceConflictError({
              resourceType: GENIE_RESOURCE_TYPE,
              resourceId: agentId,
              expectedFingerprint,
              actualFingerprint: current.fingerprint,
            });
          }
          await admin.delete(agentId, contextSignal(context));
          return { deleted: true, agentId };
        },
      ),
    );
  }
  return tools;
}

export async function normalizeDatabricksGenieAgentSpec(
  spec: DatabricksGenieAgentSpecV2,
): Promise<DatabricksGenieAgentSpecV2> {
  if (spec.version !== SERIALIZED_VERSION)
    throw new Error(`Databricks Genie serialized_space version must be ${SERIALIZED_VERSION}.`);
  const title = boundedString(spec.title, "title");
  const parentPath = boundedString(spec.parentPath, "parentPath");
  if (!parentPath.startsWith("/"))
    throw new Error("Databricks Genie parentPath must start with '/'.");
  const warehouseId = boundedString(spec.warehouseId, "warehouseId");
  if (spec.dataSources.length === 0)
    throw new Error("Databricks Genie requires at least one data source.");
  if (spec.dataSources.length > 30)
    throw new Error("Databricks Genie supports at most 30 data sources.");
  if ((spec.instructions?.length ?? 0) > 1)
    throw new Error("Databricks Genie supports at most one text instruction.");

  const dataSources = spec.dataSources
    .map(normalizeDataSource)
    .sort((a, b) => a.table.localeCompare(b.table));
  assertUnique(
    dataSources.map((source) => `${source.kind ?? "table"}:${source.table}`),
    "data source",
  );

  const sampleQuestions = await Promise.all(
    (spec.sampleQuestions ?? []).map(async (sample) => ({
      id: sample.id
        ? validateId(sample.id, "sample question")
        : await stableId("sample-question", sample.question),
      question: boundedString(sample.question, "sample question"),
    })),
  );
  sampleQuestions.sort((a, b) => a.id.localeCompare(b.id));
  assertUnique(
    sampleQuestions.map((sample) => sample.id),
    "sample question id",
  );

  const examples = await Promise.all(
    (spec.examples ?? []).map(async (item) => ({
      id: item.id
        ? validateId(item.id, "SQL example")
        : await stableId("sql-example", `${item.question}\n${item.sql}`),
      question: boundedString(item.question, "SQL example question"),
      sql: boundedString(item.sql, "SQL example"),
    })),
  );
  const sqlFunctions = await Promise.all(
    (spec.sqlFunctions ?? []).map(async (item) => ({
      id: item.id
        ? validateId(item.id, "SQL function")
        : await stableId("sql-function", item.identifier),
      identifier: qualifiedIdentifier(item.identifier, "SQL function"),
    })),
  );
  const joins = await Promise.all(
    (spec.joins ?? []).map(async (item) => {
      const left = normalizeJoinSide(item.left, "left join side");
      const right = normalizeJoinSide(item.right, "right join side");
      const sql = boundedString(item.sql, "join SQL");
      return {
        id: item.id
          ? validateId(item.id, "join")
          : await stableId("join", `${left.identifier}\n${right.identifier}\n${sql}`),
        left,
        right,
        sql,
      };
    }),
  );
  const sqlSnippets = await normalizeSqlSnippets(spec.sqlSnippets);
  const benchmarks = await Promise.all(
    (spec.benchmarks ?? []).map(async (item) => ({
      id: item.id
        ? validateId(item.id, "benchmark")
        : await stableId("benchmark", `${item.question}\n${item.sql}`),
      question: boundedString(item.question, "benchmark question"),
      sql: boundedString(item.sql, "benchmark SQL"),
    })),
  );
  for (const [name, values] of [
    ["SQL example id", examples],
    ["SQL function id", sqlFunctions],
    ["join id", joins],
    ["benchmark id", benchmarks],
  ] as const)
    assertUnique(
      values.map((item) => item.id),
      name,
    );

  return {
    version: 2,
    title,
    ...optional("description", optionalBoundedString(spec.description, "description")),
    parentPath,
    warehouseId,
    ...(sampleQuestions.length ? { sampleQuestions } : {}),
    dataSources,
    ...(spec.instructions?.length
      ? {
          instructions: spec.instructions.map((instruction) =>
            boundedString(instruction, "instruction"),
          ),
        }
      : {}),
    ...(examples.length ? { examples } : {}),
    ...(sqlFunctions.length ? { sqlFunctions } : {}),
    ...(joins.length ? { joins } : {}),
    ...(sqlSnippets ? { sqlSnippets } : {}),
    ...(benchmarks.length ? { benchmarks } : {}),
  };
}

export async function validateDatabricksGenieSqlReferences(
  spec: DatabricksGenieAgentSpecV2,
  policy: DatabricksGenieSqlPolicy,
): Promise<void> {
  const declaredDataSources = spec.dataSources.map((source) =>
    qualifiedIdentifier(source.table, "data source"),
  );
  const allowed = new Set(declaredDataSources);
  const statements: Array<{ sql: string; purpose: DatabricksGenieSqlPolicyInput["purpose"] }> = [
    ...(spec.examples ?? []).map((item) => ({ sql: item.sql, purpose: "example" as const })),
    ...(spec.joins ?? []).map((item) => ({ sql: item.sql, purpose: "join" as const })),
    ...(spec.sqlSnippets?.filters ?? []).map((item) => ({
      sql: item.sql,
      purpose: "filter" as const,
    })),
    ...(spec.sqlSnippets?.expressions ?? []).map((item) => ({
      sql: item.sql,
      purpose: "expression" as const,
    })),
    ...(spec.sqlSnippets?.measures ?? []).map((item) => ({
      sql: item.sql,
      purpose: "measure" as const,
    })),
    ...(spec.benchmarks ?? []).map((item) => ({ sql: item.sql, purpose: "benchmark" as const })),
  ];
  for (const statement of statements) {
    const result = await policy({
      sql: boundedString(statement.sql, `${statement.purpose} SQL`),
      purpose: statement.purpose,
      declaredDataSources: [...declaredDataSources],
    });
    if (!result || !Array.isArray(result.resources))
      throw new Error("Databricks Genie SQL policy must return a complete resources array.");
    for (const resource of result.resources) {
      const qualified = qualifiedIdentifier(resource, "SQL policy resource");
      if (!allowed.has(qualified))
        throw new Error(`Databricks Genie SQL policy denied undeclared resource '${qualified}'.`);
    }
  }
}

export async function serializeDatabricksGenieAgentSpec(
  spec: DatabricksGenieAgentSpecV2,
): Promise<string> {
  return JSON.stringify(serializedSpec(await normalizeDatabricksGenieAgentSpec(spec)));
}

function serializedSpec(spec: DatabricksGenieAgentSpecV2): Record<string, unknown> {
  const tables = spec.dataSources.filter((source) => (source.kind ?? "table") === "table");
  const metricViews = spec.dataSources.filter((source) => source.kind === "metric-view");
  return {
    version: 2,
    config: {
      sample_questions: (spec.sampleQuestions ?? []).map((sample) => ({
        id: sample.id,
        question: [sample.question],
      })),
    },
    data_sources: {
      tables: tables.map(dataSourceToWire),
      ...(metricViews.length ? { metric_views: metricViews.map(dataSourceToWire) } : {}),
    },
    ...((spec.instructions?.length ?? 0) ||
    (spec.examples?.length ?? 0) ||
    (spec.sqlFunctions?.length ?? 0) ||
    (spec.joins?.length ?? 0) ||
    spec.sqlSnippets
      ? {
          instructions: {
            ...(spec.instructions?.length
              ? {
                  text_instructions: spec.instructions.map((content, index) => ({
                    id: instructionId(spec, content, index),
                    content: [content],
                  })),
                }
              : {}),
            ...(spec.examples?.length
              ? {
                  example_question_sqls: spec.examples.map((item) => ({
                    id: item.id,
                    question: [item.question],
                    sql: [item.sql],
                  })),
                }
              : {}),
            ...(spec.sqlFunctions?.length
              ? {
                  sql_functions: spec.sqlFunctions.map((item) => ({
                    id: item.id,
                    identifier: item.identifier,
                  })),
                }
              : {}),
            ...(spec.joins?.length
              ? {
                  join_specs: spec.joins.map((item) => ({
                    id: item.id,
                    left: item.left,
                    right: item.right,
                    sql: [item.sql],
                  })),
                }
              : {}),
            ...(spec.sqlSnippets ? { sql_snippets: serializeSqlSnippets(spec.sqlSnippets) } : {}),
          },
        }
      : {}),
    ...(spec.benchmarks?.length
      ? {
          benchmarks: {
            questions: spec.benchmarks.map((item) => ({
              id: item.id,
              question: [item.question],
              answer: [{ format: "SQL", content: [item.sql] }],
            })),
          },
        }
      : {}),
  };
}

function normalizeJoinSide(side: DatabricksGenieJoinSide, name: string): DatabricksGenieJoinSide {
  return {
    identifier: qualifiedIdentifier(side.identifier, name),
    ...optional("alias", optionalBoundedString(side.alias, `${name} alias`)),
  };
}

async function normalizeSqlSnippets(
  snippets: DatabricksGenieSqlSnippets | undefined,
): Promise<DatabricksGenieSqlSnippets | undefined> {
  if (!snippets) return undefined;
  const normalize = async (
    values: DatabricksGenieSqlSnippet[] | undefined,
    kind: "filter" | "expression" | "measure",
  ): Promise<DatabricksGenieSqlSnippet[]> => {
    const normalized = await Promise.all(
      (values ?? []).map(async (item) => ({
        id: item.id
          ? validateId(item.id, `${kind} snippet`)
          : await stableId(
              `${kind}-snippet`,
              `${item.alias ?? item.displayName ?? ""}\n${item.sql}`,
            ),
        sql: boundedString(item.sql, `${kind} SQL`),
        ...optional("displayName", optionalBoundedString(item.displayName, `${kind} display name`)),
        ...optional("alias", optionalBoundedString(item.alias, `${kind} alias`)),
        ...(item.synonyms
          ? { synonyms: item.synonyms.map((value) => boundedString(value, `${kind} synonym`)) }
          : {}),
      })),
    );
    if ((kind === "expression" || kind === "measure") && normalized.some((item) => !item.alias))
      throw new Error(`Databricks Genie ${kind} snippets require alias.`);
    assertUnique(
      normalized.map((item) => item.id ?? ""),
      `${kind} snippet id`,
    );
    return normalized;
  };
  const filters = await normalize(snippets.filters, "filter");
  const expressions = await normalize(snippets.expressions, "expression");
  const measures = await normalize(snippets.measures, "measure");
  if (!(filters.length || expressions.length || measures.length)) return undefined;
  return {
    ...(filters.length ? { filters } : {}),
    ...(expressions.length ? { expressions } : {}),
    ...(measures.length ? { measures } : {}),
  };
}

function serializeSqlSnippets(snippets: DatabricksGenieSqlSnippets): Record<string, unknown> {
  const serialize = (items: DatabricksGenieSqlSnippet[] | undefined) =>
    (items ?? []).map((item) => ({
      id: item.id,
      sql: [item.sql],
      ...optional("display_name", item.displayName),
      ...optional("alias", item.alias),
      ...optional("synonyms", item.synonyms),
    }));
  return {
    ...(snippets.filters?.length ? { filters: serialize(snippets.filters) } : {}),
    ...(snippets.expressions?.length ? { expressions: serialize(snippets.expressions) } : {}),
    ...(snippets.measures?.length ? { measures: serialize(snippets.measures) } : {}),
  };
}

function qualifiedIdentifier(value: string, name: string): string {
  const normalized = boundedString(value, name);
  if (normalized.split(".").length !== 3 || normalized.split(".").some((part) => !part.trim()))
    throw new Error(`Databricks Genie ${name} '${normalized}' must be catalog.schema.object.`);
  return normalized;
}

function isGenieAbsentError(error: unknown): boolean {
  return /\b404\b|not[ _-]?found|does not exist|RESOURCE_DOES_NOT_EXIST|has been trashed/i.test(
    error instanceof Error ? error.message : String(error),
  );
}

function abortableDelay(milliseconds: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(signal.reason);
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, milliseconds);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function dataSourceToWire(source: DatabricksGenieDataSource): Record<string, unknown> {
  return {
    identifier: source.table,
    ...(source.description ? { description: [source.description] } : {}),
    ...(source.columns?.length
      ? {
          column_configs: source.columns.map((column) => ({
            column_name: column.name,
            ...(column.description ? { description: [column.description] } : {}),
            ...optional("synonyms", column.synonyms),
            ...optional("exclude", column.exclude),
            ...optional("get_example_values", column.getExampleValues),
            ...optional("enable_format_assistance", column.enableFormatAssistance),
            ...optional("enable_entity_matching", column.enableEntityMatching),
          })),
        }
      : {}),
  };
}

function normalizeDataSource(source: DatabricksGenieDataSource): DatabricksGenieDataSource {
  const table = qualifiedIdentifier(source.table, "data source");
  const columns = (source.columns ?? [])
    .map((column) => ({
      name: boundedString(column.name, "column name"),
      ...optional("description", optionalBoundedString(column.description, "column description")),
      ...(column.synonyms
        ? { synonyms: column.synonyms.map((synonym) => boundedString(synonym, "column synonym")) }
        : {}),
      ...optional("exclude", column.exclude),
      ...optional("getExampleValues", column.getExampleValues),
      ...optional("enableFormatAssistance", column.enableFormatAssistance),
      ...optional("enableEntityMatching", column.enableEntityMatching),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  assertUnique(
    columns.map((column) => column.name),
    `column in ${table}`,
  );
  return {
    kind: source.kind ?? "table",
    table,
    ...optional(
      "description",
      optionalBoundedString(source.description, "data source description"),
    ),
    ...(columns.length ? { columns } : {}),
  };
}

async function parseAgent(raw: RawGenieAgent): Promise<DatabricksGenieAgent> {
  const agentId = nonEmpty(asString(raw.space_id ?? raw.id) ?? "", "response space_id");
  const title = nonEmpty(asString(raw.title) ?? "", "response title");
  const parentPath = asString(raw.parent_path);
  const serializedSpace = asString(raw.serialized_space);
  const managedConfiguration = {
    agentId,
    title,
    description: asString(raw.description),
    parentPath: fingerprintWorkspacePath(parentPath),
    warehouseId: asString(raw.warehouse_id),
    // Databricks may reformat serialized_space without changing its JSON value. Fingerprint the
    // parsed value so transport whitespace cannot create a false ownership conflict.
    serializedSpace: fingerprintSerializedSpace(serializedSpace),
  };
  const etag = asString(raw.etag);
  return {
    agentId,
    title,
    ...optional("description", managedConfiguration.description),
    ...optional("parentPath", parentPath),
    ...optional("warehouseId", managedConfiguration.warehouseId),
    ...optional("etag", etag),
    ...optional("serializedSpace", serializedSpace),
    // Genie can rotate its transport ETag independently of managed configuration. Keep using the
    // latest ETag for the service's optimistic update, but do not turn operational ETag drift into
    // a false Harness ownership conflict.
    fingerprint: await databricksResourceFingerprint(managedConfiguration),
  };
}

function fingerprintSerializedSpace(value: string | undefined): unknown {
  if (value === undefined) return undefined;
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return value;
  }
}

function fingerprintWorkspacePath(value: string | undefined): string | undefined {
  if (value === "/Workspace") return "/";
  return value?.startsWith("/Workspace/") ? value.slice("/Workspace".length) : value;
}

function parsePermissions(raw: PermissionsResponse): DatabricksGeniePermissions {
  return {
    ...optional("objectId", raw.objectId),
    ...optional("objectType", raw.objectType),
    accessControlList: (raw.accessControlList ?? []).map((entry: AccessControlResponse) => ({
      ...(entry.principalName?.$case === "userName"
        ? { userName: entry.principalName.userName }
        : entry.principalName?.$case === "groupName"
          ? { groupName: entry.principalName.groupName }
          : entry.principalName?.$case === "servicePrincipalName"
            ? { servicePrincipalName: entry.principalName.servicePrincipalName }
            : {}),
      ...optional("displayName", entry.displayName),
      allPermissions: (entry.allPermissions ?? []).map((permission: Permission) => ({
        ...optional("permissionLevel", permission.permissionLevel),
        ...optional("inherited", permission.inherited),
        ...optional("inheritedFromObject", permission.inheritedFromObject),
      })),
    })),
  };
}

function permissionChangeToSdk(change: DatabricksGeniePermissionChange): AccessControlRequest {
  const principals = [change.userName, change.groupName, change.servicePrincipalName].filter(
    Boolean,
  );
  if (principals.length !== 1)
    throw new Error("Databricks Genie permission change requires exactly one principal.");
  const principalName: AccessControlRequest["principalName"] = change.userName
    ? { $case: "userName", userName: change.userName }
    : change.groupName
      ? { $case: "groupName", groupName: change.groupName }
      : change.servicePrincipalName
        ? {
            $case: "servicePrincipalName",
            servicePrincipalName: change.servicePrincipalName,
          }
        : undefined;
  return {
    permissionLevel: change.permissionLevel,
    principalName,
  };
}

function validateSerializedSpace(serializedSpace: string): void {
  let value: unknown;
  try {
    value = JSON.parse(serializedSpace);
  } catch {
    throw new Error("Databricks Genie serializedSpace must be valid JSON.");
  }
  const record = requireRecord(value, "serializedSpace");
  if (record.version !== SERIALIZED_VERSION)
    throw new Error(`Databricks Genie serializedSpace version must be ${SERIALIZED_VERSION}.`);
}

function requireRecord(value: unknown, name: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value))
    throw new Error(`Databricks Genie ${name} must be an object.`);
  return value as Record<string, unknown>;
}

function nonEmpty(value: string, name: string): string {
  const normalized = value.trim();
  if (!normalized) throw new Error(`Databricks Genie ${name} cannot be empty.`);
  return normalized;
}

function boundedString(value: string, name: string): string {
  const normalized = nonEmpty(value, name);
  if (normalized.length > MAX_STRING_LENGTH)
    throw new Error(`Databricks Genie ${name} exceeds ${MAX_STRING_LENGTH} characters.`);
  return normalized;
}

function optionalBoundedString(value: string | undefined, name: string): string | undefined {
  return value === undefined ? undefined : boundedString(value, name);
}

function validateId(id: string, name: string): string {
  if (!ID_PATTERN.test(id))
    throw new Error(`Databricks Genie ${name} id must be 32 lowercase hexadecimal characters.`);
  return id;
}

async function stableId(namespace: string, value: string): Promise<string> {
  return (await databricksResourceFingerprint({ namespace, value })).slice(0, 32);
}

function instructionId(spec: DatabricksGenieAgentSpecV2, content: string, index: number): string {
  // The normalized spec permits one instruction; derive a deterministic 32-hex identifier without
  // introducing another asynchronous serialization pass.
  const seed = `${spec.title}\u0000${content}\u0000${index}`;
  let output = "";
  for (let block = 0; block < 4; block += 1) {
    let hash = 2166136261 ^ block;
    for (let offset = 0; offset < seed.length; offset += 1) {
      hash ^= seed.charCodeAt(offset);
      hash = Math.imul(hash, 16777619);
    }
    output += (hash >>> 0).toString(16).padStart(8, "0");
  }
  return output;
}

function assertUnique(values: string[], name: string): void {
  if (new Set(values).size !== values.length)
    throw new Error(`Databricks Genie ${name} values must be unique.`);
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function signalOption(signal?: AbortSignal): { signal?: AbortSignal } {
  return signal ? { signal } : {};
}

function snakeRecord(value: unknown): Record<string, unknown> {
  return snakeValue(value) as Record<string, unknown>;
}

function snakeValue(value: unknown): unknown {
  if (typeof value === "bigint") {
    const number = Number(value);
    return Number.isSafeInteger(number) ? number : value.toString();
  }
  if (Array.isArray(value)) return value.map(snakeValue);
  if (value && typeof value === "object")
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`),
        snakeValue(item),
      ]),
    );
  return value;
}

function optional<Key extends string, Value>(
  key: Key,
  value: Value | undefined,
): Partial<Record<Key, Value>> {
  return value === undefined ? {} : ({ [key]: value } as Record<Key, Value>);
}

function genieTool(
  name: string,
  description: string,
  inputSchema: JsonObject,
  resources: JsonObject[],
  effect: "read" | "write",
  execute: (input: JsonObject, context?: ToolContext) => Promise<unknown>,
): ToolDef {
  return {
    name,
    description,
    inputSchema,
    metadata: {
      effect,
      provider: "databricks",
      service: "genie",
      governance: { resources },
    },
    execute,
  };
}

function genieSpecSchema(includeSql = false): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      version: { type: "number", const: 2 },
      title: { type: "string" },
      description: { type: "string" },
      parentPath: { type: "string" },
      warehouseId: { type: "string" },
      sampleQuestions: {
        type: "array",
        items: {
          type: "object",
          additionalProperties: false,
          properties: { id: { type: "string" }, question: { type: "string" } },
          required: ["question"],
        },
      },
      dataSources: {
        type: "array",
        minItems: 1,
        maxItems: 30,
        items: {
          type: "object",
          additionalProperties: false,
          properties: {
            kind: { type: "string", enum: ["table", "metric-view"] },
            table: { type: "string" },
            description: { type: "string" },
            columns: {
              type: "array",
              items: {
                type: "object",
                additionalProperties: false,
                properties: {
                  name: { type: "string" },
                  description: { type: "string" },
                  synonyms: { type: "array", items: { type: "string" } },
                  exclude: { type: "boolean" },
                  getExampleValues: { type: "boolean" },
                  enableFormatAssistance: { type: "boolean" },
                  enableEntityMatching: { type: "boolean" },
                },
                required: ["name"],
              },
            },
          },
          required: ["table"],
        },
      },
      instructions: { type: "array", maxItems: 1, items: { type: "string" } },
      ...(includeSql
        ? {
            examples: { type: "array", items: sqlQuestionSchema() },
            sqlFunctions: {
              type: "array",
              items: {
                type: "object",
                additionalProperties: false,
                properties: { id: { type: "string" }, identifier: { type: "string" } },
                required: ["identifier"],
              },
            },
            joins: {
              type: "array",
              items: {
                type: "object",
                additionalProperties: false,
                properties: {
                  id: { type: "string" },
                  left: joinSideSchema(),
                  right: joinSideSchema(),
                  sql: { type: "string" },
                },
                required: ["left", "right", "sql"],
              },
            },
            sqlSnippets: {
              type: "object",
              additionalProperties: false,
              properties: {
                filters: { type: "array", items: sqlSnippetSchema(false) },
                expressions: { type: "array", items: sqlSnippetSchema(true) },
                measures: { type: "array", items: sqlSnippetSchema(true) },
              },
            },
            benchmarks: { type: "array", items: sqlQuestionSchema() },
          }
        : {}),
    },
    required: ["version", "title", "parentPath", "warehouseId", "dataSources"],
  };
}

function updateToolSchema(includeSql = false): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      agentId: { type: "string" },
      expectedFingerprint: { type: "string" },
      set: {
        type: "object",
        additionalProperties: false,
        properties: {
          spec: genieSpecSchema(includeSql),
          title: { type: "string" },
          description: { type: "string" },
          parentPath: { type: "string" },
          warehouseId: { type: "string" },
        },
      },
      remove: { type: "array", items: { type: "string", enum: ["description"] } },
    },
    required: ["agentId", "expectedFingerprint", "set"],
  };
}

function sqlQuestionSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      id: { type: "string" },
      question: { type: "string" },
      sql: { type: "string" },
    },
    required: ["question", "sql"],
  };
}

function joinSideSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: { identifier: { type: "string" }, alias: { type: "string" } },
    required: ["identifier"],
  };
}

function sqlSnippetSchema(requireAlias: boolean): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      id: { type: "string" },
      sql: { type: "string" },
      displayName: { type: "string" },
      alias: { type: "string" },
      synonyms: { type: "array", items: { type: "string" } },
    },
    required: requireAlias ? ["sql", "alias"] : ["sql"],
  };
}

function idSchema(withFingerprint: boolean): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      agentId: { type: "string" },
      ...(withFingerprint ? { expectedFingerprint: { type: "string" } } : {}),
    },
    required: withFingerprint ? ["agentId", "expectedFingerprint"] : ["agentId"],
  };
}

function ownership(principal: string, context?: ToolContext): DatabricksGenieOwnershipContext {
  const toolCallId = context?.toolCallId?.trim();
  if (!toolCallId)
    throw new Error("Databricks Genie authoring requires a stable toolCallId for provenance.");
  return { principal, toolCallId };
}

function contextSignal(context?: ToolContext): { signal?: AbortSignal } {
  return signalOption(context?.signal);
}

function publicAgent(agent: DatabricksGenieAgent): Omit<DatabricksGenieAgent, "serializedSpace"> {
  const { serializedSpace: _serializedSpace, ...publicFields } = agent;
  return publicFields;
}
